import i18n from './i18n'
import { serverNow } from './lib/clock'

// Ícones de equipamento disponíveis — SVGs monocromáticos da Volis em /equipments/<label>.svg.
export const ICONS = [
  'dumper', 'excavator', 'driller', 'loader', 'motor_grader',
  'wheel_tractor', 'crawler_tractor', 'water_truck', 'fuel_truck', 'lubricant_truck',
]

// Tipos que reaproveitam o icone de outro (variantes de infra e os dozers).
const ICON_ALIAS = {
  excavator_infra: 'excavator',
  loader_infra: 'loader',
  track_dozer: 'crawler_tractor',
  wheel_dozer: 'wheel_tractor',
}

export function iconOf(label) {
  if (!label) return 'wheel_tractor'
  const key = ICON_ALIAS[label] || label
  return ICONS.includes(key) ? key : 'wheel_tractor'
}

// Imagens placeholder da máquina (Exodia, do Yu-Gi-Oh) — arte real via ygoprodeck.
export const EXODIA_IMAGES = {
  'exodia':    'https://images.ygoprodeck.com/images/cards/33396948.jpg',
  'right-arm': 'https://images.ygoprodeck.com/images/cards/70903634.jpg',
  'left-arm':  'https://images.ygoprodeck.com/images/cards/7902349.jpg',
  'right-leg': 'https://images.ygoprodeck.com/images/cards/8124921.jpg',
  'left-leg':  'https://images.ygoprodeck.com/images/cards/44519536.jpg',
}
export const EXODIA_KEYS = Object.keys(EXODIA_IMAGES)

// Severidade de item de checklist pré-operacional (padrão de mineração: A bloqueia, B oficina, C operador).
export const SEVERITIES = [
  { value: 'A', label: 'A — bloqueia operação', color: 'text-status-down' },
  { value: 'B', label: 'B — encaminha p/ oficina', color: 'text-status-stop' },
  { value: 'C', label: 'C — corrigível pelo operador', color: 'text-status-op' },
]

// Tipos de minério (mina). Estéril = material não-minério (WASTE).
export const ORE_TYPES = ['Ferro', 'Cobre', 'Ouro', 'Carvão', 'Calcário', 'Bauxita', 'Manganês', 'Níquel', 'Estéril']

// Estados de um pit (cava) de mineração.
export const PIT_STATUS = ['Ativo', 'Planejado', 'Inativo', 'Exaurido']

// Tipos de pit (cava).
export const PIT_TYPES = ['Céu aberto', 'Subterrânea', 'Cava de expansão', 'Stockpile']

// Os valores acima são gravados no banco e comparados pelo backend (ex.: o documento
// de pits do VMC só leva pits 'Ativo'), então NÃO podem ser traduzidos: o que se
// traduz é só a exibição. Valor fora do catálogo (dado do cliente) passa direto.
const ORE_KEYS = { Ferro: 'iron', Cobre: 'copper', Ouro: 'gold', Carvão: 'coal', Calcário: 'limestone', Bauxita: 'bauxite', Manganês: 'manganese', Níquel: 'nickel', Estéril: 'waste' }
const PIT_STATUS_KEYS = { Ativo: 'active', Planejado: 'planned', Inativo: 'inactive', Exaurido: 'depleted' }
const PIT_TYPE_KEYS = { 'Céu aberto': 'open_pit', Subterrânea: 'underground', 'Cava de expansão': 'expansion', Stockpile: 'stockpile' }

const labelFrom = (map, ns, value) => {
  const key = map[value]
  return key ? i18n.t(`common:${ns}.${key}`) : (value || '')
}
export const oreTypeLabel = (v) => labelFrom(ORE_KEYS, 'ore_types', v)
export const pitStatusLabel = (v) => labelFrom(PIT_STATUS_KEYS, 'pit_status', v)
export const pitTypeLabel = (v) => labelFrom(PIT_TYPE_KEYS, 'pit_types', v)

// Idiomas dos textos do checklist. São os códigos do documento que vai ao VMC
// (o operador escolhe o idioma no login), não os códigos da UI: pt-BR vira pt.
export const CHECKLIST_LANGS = ['en', 'pt', 'fr', 'es', 'hy']

export function checklistLang(uiLang) {
  const code = String(uiLang || '').slice(0, 2).toLowerCase()
  return CHECKLIST_LANGS.includes(code) ? code : 'en'
}

// Categorias do checklist (agrupam os itens no stepper da HMI). São FIXAS no
// produto: o documento vai ao VMC só com código e ordem, e o VMC (mock e app
// oficial) tem os nomes fixos nos 5 idiomas. Categoria fora da lista (checklist
// legado) cai nos names gravados no documento ou no próprio código.
export const CHECKLIST_CATEGORIES = [
  { code: 'exterior', order: 1 },
  { code: 'fluids', order: 2 },
  { code: 'cabin', order: 3 },
]

const CHECKLIST_CATEGORY_NAMES = {
  exterior: { en: 'Structure and exterior', pt: 'Estrutura e exterior', fr: 'Structure et extérieur', es: 'Estructura y exterior', hy: 'Կառուցվածք և արտաքին' },
  fluids: { en: 'Fluid system', pt: 'Sistema de fluidos', fr: 'Système de fluides', es: 'Sistema de fluidos', hy: 'Հեղուկների համակարգ' },
  cabin: { en: 'Cab and controls', pt: 'Cabine e controlos', fr: 'Cabine et commandes', es: 'Cabina y controles', hy: 'Խցիկ և կառավարում' },
}

export function checklistCatName(cat, lang) {
  if (!cat) return ''
  const code = typeof cat === 'string' ? cat : cat.code
  const fixed = CHECKLIST_CATEGORY_NAMES[code]
  if (fixed) return fixed[lang] || fixed.en
  return cat.names?.[lang] || cat.names?.en || code || ''
}

export function checklistItemTitle(item, lang) {
  if (!item) return ''
  return item.texts?.[lang] || item.texts?.en || item.text || item.item || ''
}

// Fallback para devices legados (adotados antes do cadastro de tipos): vehicle_type
// numérico. O rótulo é traduzido (o nome do tipo cadastrado, não: é dado do cliente).
const LEGACY = {
  0:  { key: 'dumper',    svg: 'dumper' },
  1:  { key: 'excavator', svg: 'excavator' },
  2:  { key: 'driller',   svg: 'driller' },
  3:  { key: 'loader',    svg: 'loader' },
  99: { key: 'generic',   svg: 'wheel_tractor' },
}
const legacyLabel = (key) => i18n.t(`common:equipment_types.${key}`)

function parseForm(device) {
  let f = device?.formData
  if (typeof f === 'string') {
    try { f = JSON.parse(f) } catch { f = null }
  }
  return f || {}
}

// Índice de categorias por id e de mina/pit por equipamento, montado UMA vez por
// versão das listas (as arrays só trocam de identidade no reloadAdmin). Sem ele o
// equipmentOf varria minas e pits a cada linha, dentro de comparadores de ordenação
// e na cadência do WebSocket.
let indexCache = null
function equipmentIndex(categories, mines) {
  if (indexCache && indexCache.categories === categories && indexCache.mines === mines) return indexCache

  const catById = new Map()
  for (const c of categories || []) catById.set(c.id, c)

  // Primeiro dono vence, na mesma ordem da varredura antiga: mina por mina, pit
  // antes da lista legada da própria mina.
  const ownerOf = new Map()
  for (const m of mines || []) {
    for (const p of Array.isArray(m.pits) ? m.pits : []) {
      for (const name of Array.isArray(p.equipment_ids) ? p.equipment_ids : []) {
        if (!ownerOf.has(name)) ownerOf.set(name, { mine: m.name, pit: p.name || '' })
      }
    }
    for (const name of Array.isArray(m.equipment_ids) ? m.equipment_ids : []) {
      if (!ownerOf.has(name)) ownerOf.set(name, { mine: m.name, pit: '' })
    }
  }

  indexCache = { categories, mines, catById, ownerOf }
  return indexCache
}

export function equipmentOf(device, categories = [], mines = []) {
  const f = parseForm(device)
  const { catById, ownerOf } = equipmentIndex(categories, mines)

  // Tipo de máquina: resolve AO VIVO pelo category_id (reflete edições na Frota);
  // fallback ao snapshot do form_data; fallback legado (vehicle_type).
  let svg, label, model = '', manufacturer = ''
  const cat = f.category_id != null ? catById.get(f.category_id) : null
  if (cat) {
    svg = iconOf(cat.label); label = cat.name; model = cat.model || ''; manufacturer = cat.manufacturer || ''
  } else if (f.category_label) {
    svg = iconOf(f.category_label); label = f.category_name || legacyLabel('generic')
  } else {
    const t = LEGACY[f.vehicle_type] ?? LEGACY[99]
    svg = t.svg; label = legacyLabel(t.key); model = f.model || ''
  }

  // Mina / Pit: resolve AO VIVO pela hierarquia Mina › Pit › Equipamentos (o equipamento
  // está nos equipment_ids de um pit). Fallback ao equipment_ids legado da mina e ao form
  // da adoção (f.mine é string no legado, objeto {id,name} na adoção nova).
  let mine = (typeof f.mine === 'string' ? f.mine : f.mine?.name) || ''
  let pit = f.pit?.name || ''
  const owning = ownerOf.get(device.name)
  if (owning) {
    mine = owning.mine
    pit = owning.pit
  }

  return { svg: svg || 'wheel_tractor', label: label || legacyLabel('generic'), name: f.name || '', model, manufacturer, mine, pit }
}

// Ordem de exibição estável por status: em operação → parado → sem telemetria →
// sem comunicação. Evita que os cards troquem de lugar a cada atualização do WS.
// Uma paragem aberta é o seu próprio estado, entre "em operação" e "parado": a
// máquina tem operador na cabine (não está parada por abandono) mas NÃO produz.
export const STOP_STATUS = {
  delay:          { key: 'stop_delay',     dot: 'bg-status-delay',     text: 'text-status-delay',     bar: 'bg-status-delay',     soft: 'bg-status-delay/10' },
  non_productive: { key: 'stop_nonprod',   dot: 'bg-status-nonprod',   text: 'text-status-nonprod',   bar: 'bg-status-nonprod',   soft: 'bg-status-nonprod/10' },
  standby:        { key: 'stop_standby',   dot: 'bg-status-standby',   text: 'text-status-standby',   bar: 'bg-status-standby',   soft: 'bg-status-standby/10' },
  downtime:       { key: 'stop_breakdown', dot: 'bg-status-breakdown', text: 'text-status-breakdown', bar: 'bg-status-breakdown', soft: 'bg-status-breakdown/10' },
}

export const STATUS_ORDER = {
  op: 0,
  stop_delay: 1, stop_nonprod: 2, stop_standby: 3, stop_breakdown: 4,
  stop: 5, notelem: 6, down: 7,
}
// Comparador: status primeiro e, dentro de cada status, alfabético pelo nome
// (nameOf(device) deve devolver o nome exibido — amigável ou o vmc-id).
export function compareDevices(nameOf) {
  return (a, b) => {
    const sa = STATUS_ORDER[statusOf(a).key] ?? 9
    const sb = STATUS_ORDER[statusOf(b).key] ?? 9
    if (sa !== sb) return sa - sb
    return (nameOf(a) || '').localeCompare(nameOf(b) || '', 'pt-BR')
  }
}

// Estados operacionais. Operação é por SESSÃO DE LOGIN, não velocidade.
// Prioridade (login vence telemetria — um operador logado está em operação mesmo
// que a telemetria oscile):
//  vermelho = sem comunicação  → nenhuma mensagem (qualquer) há ≥75s
//  verde    = em operação      → operador logado
//  laranja  = parado           → sem login, telemetria recente
//  roxo     = sem telemetria   → sem login, comunicando (health/evento) mas sem telemetria há ≥15s
// A janela de comunicação é de 75 s porque o VMC 0.8 sem fix de GPS só manda o health,
// a cada 30 s: com os 15 s antigos ele piscava em "sem comunicação" entre dois sinais
// de vida. É a mesma janela do `online` do backend.
export const COMMS_TIMEOUT_S = 75
export const TELEMETRY_TIMEOUT_S = 15

export function telemetryFresh(device, now = serverNow()) {
  if (!device?.lastTelemetryAt) return false
  return (now - new Date(device.lastTelemetryAt)) / 1000 < TELEMETRY_TIMEOUT_S
}

export function statusOf(device) {
  // serverNow, e não Date.now: os instantes comparados aqui (lastSeen,
  // lastTelemetryAt) são carimbados pelo SERVIDOR. Com o relógio da estação, uma
  // máquina atrasada pintava a frota inteira de "sem comunicação" com todos os
  // equipamentos transmitindo.
  const now = serverNow()
  const seenSecs = (now - new Date(device.lastSeen)) / 1000
  if (seenSecs >= COMMS_TIMEOUT_S) {
    return { key: 'down', label: 'Sem comunicação', dot: 'bg-status-down', text: 'text-status-down', bar: 'bg-status-down', soft: 'bg-status-down/10', pulse: false }
  }
  // Paragem aberta vence o "em operação": com o operador logado e a máquina parada
  // para almoço ou avaria, o dashboard pintava tudo de verde e o cartão dizia só
  // "Parada", sem dizer de quê. O balde vem do backend (devices.stop_bucket) e a cor
  // é a mesma do modelo de tempo GMG das telas de produção.
  const stop = device.stopBucket ? (STOP_STATUS[device.stopBucket] || STOP_STATUS.delay) : null
  if (stop) {
    return { ...stop, label: device.stopReason || 'Paragem', bucket: device.stopBucket, reason: device.stopReason || '', pulse: false }
  }
  if ((device.operatorName ?? '') !== '') {
    return { key: 'op', label: 'Em operação', dot: 'bg-status-op', text: 'text-status-op', bar: 'bg-status-op', soft: 'bg-status-op/10', pulse: true }
  }
  if (telemetryFresh(device, now)) {
    return { key: 'stop', label: 'Parado', dot: 'bg-status-stop', text: 'text-status-stop', bar: 'bg-status-stop', soft: 'bg-status-stop/10', pulse: false }
  }
  return { key: 'notelem', label: 'Sem telemetria / Online', dot: 'bg-violet-500', text: 'text-violet-600', bar: 'bg-violet-500', soft: 'bg-violet-500/10', pulse: false }
}
